export interface TelegramWebAppUser {
  id: number;
  is_bot?: boolean;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
  allows_write_to_pm?: boolean;
  photo_url?: string;
}

export interface TelegramWebAppChat {
  id: number;
  type: string;
  title?: string;
  username?: string;
  photo_url?: string;
}

export interface TelegramWebAppInitData {
  query_id?: string;
  user?: TelegramWebAppUser;
  receiver?: TelegramWebAppUser;
  chat?: TelegramWebAppChat;
  chat_type?: string;
  chat_instance?: string;
  start_param?: string;
  can_send_after?: number;
  auth_date?: number;
  hash?: string;
  signature?: string;
}

export interface TelegramThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
  header_bg_color?: string;
  accent_text_color?: string;
  section_bg_color?: string;
  section_header_text_color?: string;
  subtitle_text_color?: string;
  destructive_text_color?: string;
}

export type TelegramColorScheme = 'light' | 'dark';
export type TelegramEventHandler = (...args: unknown[]) => void;

export interface TelegramSafeAreaInset {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface TelegramBackButton {
  isVisible: boolean;
  show: () => TelegramBackButton;
  hide: () => TelegramBackButton;
  onClick: (callback: () => void) => TelegramBackButton;
  offClick: (callback: () => void) => TelegramBackButton;
}

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: TelegramWebAppInitData;
  version: string;
  platform: string;
  colorScheme: TelegramColorScheme;
  themeParams: TelegramThemeParams;
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  safeAreaInset: TelegramSafeAreaInset;
  contentSafeAreaInset: TelegramSafeAreaInset;
  headerColor: string;
  backgroundColor: string;
  isClosingConfirmationEnabled: boolean;
  BackButton: TelegramBackButton;
  ready: () => void;
  expand: () => void;
  close: () => void;
  onEvent: (eventType: string, eventHandler: TelegramEventHandler) => void;
  offEvent: (eventType: string, eventHandler: TelegramEventHandler) => void;
  sendData: (data: string) => void;
  openLink: (url: string, options?: { try_instant_view?: boolean }) => void;
  openTelegramLink: (url: string) => void;
}

interface TelegramWindow extends Window {
  Telegram?: {
    WebApp?: TelegramWebApp;
  };
}

function browserWindow() {
  return typeof window === 'undefined' ? undefined : window;
}

function getNativeTelegramWebApp(target: Window | undefined = browserWindow()) {
  return (target as TelegramWindow | undefined)?.Telegram?.WebApp ?? null;
}

export function isRealTelegramWebApp(target: Window | undefined = browserWindow()) {
  const webApp = getNativeTelegramWebApp(target);
  return Boolean(webApp?.initData || webApp?.initDataUnsafe?.user);
}

export function getTelegramWebApp(target: Window | undefined = browserWindow()) {
  const nativeWebApp = getNativeTelegramWebApp(target);
  return isRealTelegramWebApp(target) ? nativeWebApp : null;
}

export function hasTelegramWebApp(target: Window | undefined = browserWindow()) {
  return Boolean(getTelegramWebApp(target));
}

export function getTelegramInitData() {
  return getTelegramWebApp()?.initData ?? '';
}

export function getTelegramUser() {
  return getTelegramWebApp()?.initDataUnsafe?.user ?? null;
}

export function getTelegramThemeParams() {
  return getTelegramWebApp()?.themeParams ?? {};
}

const zeroInset: TelegramSafeAreaInset = { top: 0, bottom: 0, left: 0, right: 0 };

export function getTelegramSafeAreaInsets() {
  const webApp = getTelegramWebApp();

  return {
    safeArea: webApp?.safeAreaInset ?? zeroInset,
    contentSafeArea: webApp?.contentSafeAreaInset ?? zeroInset,
  };
}

export function getTelegramViewport() {
  const webApp = getTelegramWebApp();

  return {
    height: webApp?.viewportHeight ?? null,
    stableHeight: webApp?.viewportStableHeight ?? null,
    isExpanded: webApp?.isExpanded ?? false,
  };
}

export function initializeTelegramWebApp(options: { expand?: boolean } = {}) {
  const webApp = getTelegramWebApp();
  if (!webApp) return null;

  webApp.ready();
  if (options.expand) {
    webApp.expand();
  }

  return webApp;
}
